import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, map, Observable, of, throwError, mergeMap, iif } from 'rxjs';
import { PPUSuccessResponse } from '../models/response.model';
import { LocationPermissionModel, PPULocationModel } from '../models/location.model';
import { AuthService } from './auth.service';
import { LocationStateService } from './location-state.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class LocationService {
  public locations: Array<PPULocationModel> | null = null;
  public corporateChildLocations: Array<PPULocationModel> | null = null;
  public corporateLocations: Array<PPULocationModel> | null = null;
  private permission: LocationPermissionModel | null = null;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private locationState: LocationStateService,
    private translate: TranslateService
  ) {}

  public GetLocationPermission(ppuLocationId: number = 0, refresh: boolean = false): Observable<LocationPermissionModel> {
    if (this.permission && !refresh) {
      return of(this.permission);
    }
    return this.http
      .get<PPUSuccessResponse<LocationPermissionModel>>(
        `${environment.API_ROOT}Agent/api/v1/PPULocation/GetLocationPermission?PPULocationId=${ppuLocationId}`
      )
      .pipe(
        map((res) => {
          this.permission = res.result as LocationPermissionModel;
          return this.permission;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  public GetSatelliteLocations(refresh: boolean = false): Observable<Array<PPULocationModel>> {
    if (this.locations && !refresh) {
      return of(this.locations);
    }
    return this.http
      .get<PPUSuccessResponse<PPULocationModel>>(`${environment.API_ROOT}Agent/api/v1/PPULocation/SatelliteLocations`)
      .pipe(
        map((res) => {
          this.locations = res.results;
          return this.locations;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  public GetCorporateChildLocations(refresh: boolean = false): Observable<Array<PPULocationModel>> {
    if (this.corporateChildLocations && !refresh) {
      return of(this.corporateChildLocations);
    }

    return this.http
      .get<PPUSuccessResponse<PPULocationModel>>(
        `${environment.API_ROOT}Agent/api/v1/PPULocation/CorporateChildLocations`
      )
      .pipe(
        map((res) => {
          this.corporateChildLocations = res.results;
          return this.corporateChildLocations;
        }),
        catchError((error) => throwError(() => error))
      );
  }

  public GetCorporateLocations(refresh: boolean = false, includeLocationId: number | null = null): Observable<Array<PPULocationModel>> {
    if (this.corporateLocations && !refresh &&
      (!includeLocationId || this.corporateLocations.some(location => location.id === includeLocationId))) {
      return of(this.corporateLocations);
    }

    return this.http
      .get<PPUSuccessResponse<PPULocationModel>>(
        `${environment.API_ROOT}Agent/api/v1/PPULocation/CorporateLocations`,
        { params: includeLocationId ? { includeLocationId } : {} }
      )
      .pipe(
        map((res) => {
          this.corporateLocations = res.results;
          return this.corporateLocations;
        }),
        catchError((error) => throwError(() => error))
      );
  }

  public GetUserLocationPermission() {
    return this.authService.isAdminRole().pipe(
      mergeMap((value) =>
        iif(
          () => value,
          this.GetSatelliteLocations().pipe(
            mergeMap((result) => {
              if (!result.length) {
                // A corporate account must have at least one assigned child location.
                return throwError(() => [this.translate.instant('App.NoCorporateLocationsAssigned')]);
              }

              const savedId = this.locationState.ppuLocationId();
              const locationId = savedId && result.some(l => l.id === savedId)
                ? savedId
                : result[0].id;
              return this.GetLocationPermission(locationId);
            })
          ),
          this.GetLocationPermission()
        )
      ),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public getLocationCode(locationId: number): string | null {
    if (this.permission?.ppuLocationId === locationId) return this.permission.locationCode;

    const location = this.locations?.find(location => location.id === locationId)
      ?? this.corporateChildLocations?.find(location => location.id === locationId);
    // Location lists split codes such as PPU#004 into addressLine2 "PPU" and storeNo 4.
    return location?.storeNo != null
      ? `${location.addressLine2}#${String(location.storeNo).padStart(3, '0')}`
      : location?.addressLine2 ?? null;
  }

  public get currentPermission(): LocationPermissionModel | null {
    return this.permission;
  }
}
